import { Box, Typography } from "@mui/material"
import { CheckOutlined, CloseOutlined, SyncOutlined } from "@mui-symbols-material/w300"
import type { FC, ReactElement } from "react"
import React, { useMemo, useRef } from "react"

import ConfirmationDialog from "../../../shared/components/common/Modals/ConfirmationDialog"
import { pluralize } from "../../../utils/stringUtils"
import { PricingDialogs } from "../constants"

import { Button } from "@/components/common/Button"
import {
  type PriceChangeSubmission,
  PriceChangeSubmissionSource,
  type UpdateSubmissionItemInput,
} from "@/graphql/codegen/graphql"
import { useApprovePriceChangeSubmission } from "@/screens/Pricing/hooks/api/useApprovePriceChangeSubmission"
import { useDenyPriceChangeSubmission } from "@/screens/Pricing/hooks/api/useDenyPriceChangeSubmission"
import { useRevertPriceChangeSubmission } from "@/screens/Pricing/hooks/api/useRevertPriceChangeSubmission"
import { useUpdatePriceChangeSubmission } from "@/screens/Pricing/hooks/api/useUpdatePriceChangeSubmission"
import { usePriceTableEditsStore, useSubmissionUpdatesStore } from "@/screens/Pricing/stores"
import { calculateTotalPricingVariations } from "@/screens/Pricing/utils"
import { checkInvalidEffectiveDates } from "@/screens/Pricing/utils/priceChangeSubmit"

interface ReviewSubmissionHeaderProps {
  priceChangeSubmission: PriceChangeSubmission
  onDeny?: () => void
  onApprove?: () => void
  onSaveAndExit?: () => void
  onRevert: (submissionId: string) => void
  setIsDialogOpen: (dialog: PricingDialogs | null) => void
  isResolved?: boolean
  isSellingPrice: boolean
  isDialogOpen: PricingDialogs | null
  hasAnyChanges: boolean
  hasMaintenancePermission: boolean
  selectedCounts: Record<string, number>
  total: number
  modifiedTiersCount: number
  totalInvalidCount?: number
  isFetching?: boolean
  collectCurrentPageItems: () => Promise<UpdateSubmissionItemInput[]>
  flushPendingEdits: () => Promise<void>
  onChangesClear: () => void
  onInvalidDatesReset: () => void
}

const ReviewSubmissionHeader: FC<ReviewSubmissionHeaderProps> = ({
  onApprove,
  onRevert,
  onDeny,
  onSaveAndExit,
  isDialogOpen,
  setIsDialogOpen,
  isResolved,
  isSellingPrice,
  hasMaintenancePermission,
  priceChangeSubmission,
  hasAnyChanges,
  selectedCounts,
  total,
  modifiedTiersCount,
  totalInvalidCount = 0,
  isFetching = false,
  collectCurrentPageItems,
  flushPendingEdits,
  onChangesClear,
  onInvalidDatesReset,
}): ReactElement => {
  const pendingActionRef = useRef<(() => void) | null>(null)
  const { updatePriceChangeSubmission, isUpdating } = useUpdatePriceChangeSubmission()
  const { approvePriceChangeSubmission, isApproving } = useApprovePriceChangeSubmission()
  const { denyPriceChangeSubmission, isDenying } = useDenyPriceChangeSubmission()
  const { revertPriceChangeSubmission, isReverting } = useRevertPriceChangeSubmission()

  const priceChangeItemsCount = useMemo(
    () => priceChangeSubmission.priceChangeItems?.length ?? 0,
    [priceChangeSubmission]
  )

  const totalPricingVariations = calculateTotalPricingVariations(priceChangeSubmission?.priceChangeItems)
  const totalSelected = (priceChangeSubmission.priceChangeItems ?? []).reduce(
    (acc, pcItems) => acc + (selectedCounts[pcItems.id] ?? 0),
    0
  )

  const isQuoteSource = priceChangeSubmission.source === PriceChangeSubmissionSource.SalesPipelineQuoteRequest
  const isSecondaryTextDisplay = !isResolved && totalPricingVariations > 0
  const denyLabel = isQuoteSource ? "Deny Quote" : "Deny Price Change"
  const approveLabel = isQuoteSource ? "Approve Quote" : "Approve Price Change"
  const isButtonDisabled =
    isFetching || isDenying || isReverting || isUpdating || isApproving || !hasMaintenancePermission
  const hasInvalidTiers = totalInvalidCount > 0

  const executeSave = async (onSuccess?: () => void): Promise<boolean> => {
    // No UI path removes an existing row in this screen yet — once one exists,
    // collect removed customerRowIds from the dirty tables here too.
    const isSuccess = await updatePriceChangeSubmission({
      submissionId: priceChangeSubmission.id,
      items: useSubmissionUpdatesStore.getState().getMergedItems(await collectCurrentPageItems()),
      removedCustomerRowIds: [],
    })

    if (isSuccess) {
      onChangesClear()
      onSuccess?.()
    }

    return isSuccess
  }

  const executeApprove = async () => {
    if (hasAnyChanges || usePriceTableEditsStore.getState().hasEdits) {
      const isSaveSuccess = await executeSave()
      if (!isSaveSuccess) {
        return
      }
    }

    const isSuccess = await approvePriceChangeSubmission({ submissionId: priceChangeSubmission.id })
    if (!isSuccess) {
      return
    }

    onApprove?.()
  }

  const confirmInvalidDatesThen = async (action: () => void) => {
    await flushPendingEdits()
    if (checkInvalidEffectiveDates(usePriceTableEditsStore.getState().edits)) {
      pendingActionRef.current = action
      setIsDialogOpen(PricingDialogs.AutoResetEffectiveDateConfirmation)
    } else {
      action()
    }
  }

  const handleSaveAndExit = () => confirmInvalidDatesThen(() => executeSave(onSaveAndExit))

  const handleApprove = () => confirmInvalidDatesThen(executeApprove)

  const handleInvalidDatesReset = () => {
    usePriceTableEditsStore.getState().resetInvalidDates()
    onInvalidDatesReset()
    setIsDialogOpen(null)
    const action = pendingActionRef.current
    pendingActionRef.current = null
    action?.()
  }

  const handleInvalidDatesCancel = () => {
    pendingActionRef.current = null
    setIsDialogOpen(null)
  }

  const handleDeny = async () => {
    if (!priceChangeSubmission?.id) {
      return
    }

    const isSuccess = await denyPriceChangeSubmission({
      submissionId: priceChangeSubmission.id,
    })
    if (!isSuccess) {
      return
    }

    setIsDialogOpen(null)
    onDeny?.()
  }

  const handleRevert = async () => {
    if (!priceChangeSubmission?.id) {
      return
    }

    const revertedSubmissionId = await revertPriceChangeSubmission({ submissionId: priceChangeSubmission.id })

    if (revertedSubmissionId) {
      onRevert(revertedSubmissionId)
    }
  }

  return (
    <Box className='flex w-full flex-row justify-between'>
      <Box className='flex flex-col gap-1'>
        {!isFetching && (
          <Typography variant='body1'>
            Showing {priceChangeItemsCount} of {total} {pluralize(total, "product", "products")},{" "}
            {totalPricingVariations ?? 0} pricing {pluralize(totalPricingVariations ?? 0, "variation", "variations")} on
            this page.
          </Typography>
        )}
        {!isFetching && isSecondaryTextDisplay && (
          <Typography variant='body2' className='text-gray-600'>
            {totalSelected} of {totalPricingVariations} selected on this page
          </Typography>
        )}
        {modifiedTiersCount > 0 && (
          <Typography variant='body2' className='text-gray-600'>
            {modifiedTiersCount} manually modified {pluralize(modifiedTiersCount, "tier", "tiers")}
          </Typography>
        )}
      </Box>
      <Box className='flex flex-col justify-end gap-3 self-start'>
        <Box className='flex gap-3 whitespace-nowrap'>
          {isResolved && isSellingPrice && !isQuoteSource && (
            <Button
              variant='error'
              disabled={isButtonDisabled || !priceChangeSubmission?.isRevertible}
              startIcon={<SyncOutlined />}
              onClick={handleRevert}
            >
              Revert Price Change
            </Button>
          )}
          {!isResolved && (
            <>
              {!isQuoteSource && (
                <Button
                  appearance='outlined'
                  onClick={handleSaveAndExit}
                  disabled={isButtonDisabled || !hasAnyChanges || hasInvalidTiers}
                >
                  Save Changes & Exit
                </Button>
              )}
              <Button
                appearance='outlined'
                variant='error'
                disabled={isButtonDisabled}
                startIcon={<CloseOutlined />}
                onClick={() => setIsDialogOpen(PricingDialogs.DenyPriceChangeConfirmationDialog)}
              >
                {denyLabel}
              </Button>
              <Button
                disabled={isButtonDisabled || hasInvalidTiers}
                appearance='contained'
                startIcon={<CheckOutlined />}
                onClick={handleApprove}
              >
                {approveLabel}
              </Button>
            </>
          )}
        </Box>
        <Box className='flex justify-end'>
          {hasInvalidTiers && !isResolved && (
            <Typography variant='body2' className='text-red-600'>
              {totalInvalidCount} required {pluralize(totalInvalidCount, "cell needs", "cells need")} attention before
              saving or approving.
            </Typography>
          )}
        </Box>
      </Box>
      {isDialogOpen === PricingDialogs.DenyPriceChangeConfirmationDialog && (
        <ConfirmationDialog
          title='Are you sure you want to deny this price change?'
          content='This action cannot be undone.'
          closeLabel='Go Back'
          confirmLabel='Deny Price Change'
          isWarning
          confirmDisabled={isDenying}
          onClose={() => setIsDialogOpen(null)}
          onConfirm={handleDeny}
        />
      )}
      {isDialogOpen === PricingDialogs.AutoResetEffectiveDateConfirmation && (
        <ConfirmationDialog
          title='Are you sure you want to update this price?'
          content='The price has an effective date in the past. Saving this change will update the effective date to today. You will not be able to undo this change.'
          onClose={handleInvalidDatesCancel}
          confirmLabel='Update Price'
          closeLabel='Cancel'
          onConfirm={handleInvalidDatesReset}
        />
      )}
    </Box>
  )
}

export default ReviewSubmissionHeader
